"use client";

import {
  Area,
  CartesianGrid,
  ComposedChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  ChartContainer,
  type ChartConfig,
} from "@/shared/ui/ChartContainer";
import { ChartTooltipContent } from "@/shared/ui/ChartTooltipContent";

export type TransferTrendChartPoint = {
  period: string;
  requestCount: number;
  qtyShipped: number;
  shippedValueLak: number;
};

type Props = {
  data: TransferTrendChartPoint[];
  config: ChartConfig;
  xAxisTickFormatter?: (value: string) => string;
  className?: string;
};

function formatCountTick(value: number) {
  const n = Number(value);
  if (Number.isNaN(n)) return String(value);
  if (n >= 1_000) return `${Math.round(n / 100) / 10}k`;
  return String(Math.round(n));
}

function formatLakTick(value: number) {
  const n = Number(value);
  if (Number.isNaN(n)) return String(value);
  if (n >= 1_000_000) return `${Math.round(n / 100_000) / 10}M`;
  if (n >= 1_000) return `${Math.round(n / 100) / 10}k`;
  return String(Math.round(n));
}

export function TransferTrendDualAxisChart({
  data,
  config,
  xAxisTickFormatter,
  className,
}: Props) {
  return (
    <ChartContainer config={config} className={className}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart
          data={data}
          margin={{ top: 8, left: 4, right: 8, bottom: 0 }}
          accessibilityLayer
        >
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="period"
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            tickFormatter={xAxisTickFormatter ?? ((v) => String(v))}
          />
          <YAxis
            yAxisId="left"
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            width={48}
            tickFormatter={(v) => formatCountTick(Number(v))}
          />
          <YAxis
            yAxisId="right"
            orientation="right"
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            width={56}
            tickFormatter={(v) => formatLakTick(Number(v))}
          />
          <Tooltip
            cursor={false}
            content={<ChartTooltipContent config={config} indicator="dot" />}
          />
          <Area
            yAxisId="left"
            type="natural"
            dataKey="requestCount"
            fill={config.requestCount?.color ?? "var(--chart-1)"}
            stroke={config.requestCount?.color ?? "var(--chart-1)"}
            fillOpacity={0.35}
          />
          <Area
            yAxisId="left"
            type="natural"
            dataKey="qtyShipped"
            fill={config.qtyShipped?.color ?? "var(--chart-2)"}
            stroke={config.qtyShipped?.color ?? "var(--chart-2)"}
            fillOpacity={0.25}
          />
          <Area
            yAxisId="right"
            type="natural"
            dataKey="shippedValueLak"
            fill={config.shippedValueLak?.color ?? "var(--chart-3)"}
            stroke={config.shippedValueLak?.color ?? "var(--chart-3)"}
            fillOpacity={0.2}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </ChartContainer>
  );
}
